import type {
  Bar,
  DatafeedErrorCallback,
  DatafeedQuoteValues,
  GetMarksCallback,
  HistoryCallback,
  IExternalDatafeed,
  LibrarySymbolInfo,
  Mark,
  OnReadyCallback,
  QuoteData,
  QuotesCallback,
  QuotesErrorCallback,
  ResolutionString,
  ResolveCallback,
  SearchSymbolsCallback,
  SubscribeBarsCallback,
} from 'charting_library/datafeed-api'

import {
  CHART_TICK_RESOLUTION,
  CHART_TIMEFRAMES,
  chartTimeframeToResolution,
  nextChartBarStartMs,
  resolutionToChartTimeframe,
} from '@/lib/chart-timeframe'
import {
  areChartSymbolsEqual,
  type ChartSymbolIdentity,
  parseQualifiedChartSymbol,
  qualifyChartSymbol,
} from '@/lib/tradingview/chart-symbol-identity'
import { ChartSeriesSubscriptionManager } from '@/lib/tradingview/datafeeds/chart-series-subscription-manager'
import {
  type CandleLike,
  type ClosedMarketBarFilter,
} from '@/lib/tradingview/datafeeds/closed-market-bars'
import {
  type SeriesInputValues,
  SeriesSourceRegistry,
} from '@/lib/tradingview/datafeeds/series-sources'
import { SeriesSubscriptionManager } from '@/lib/tradingview/datafeeds/series-subscription-manager'
import type { ChartAnalyticsMetric } from '@/types/dashboard'
import { ChartTimeframe, Exchange } from '@/types/generated/common/types'
import { CandleFamily } from '@/types/generated/public_api'

// ---------------------------------------------------------------------------
// Resolution helpers
// ---------------------------------------------------------------------------

/**
 * TV resolution → bar width in seconds (a `ChartTimeframe`); NaN off the chart grid. The
 * one-tick resolution (Tick) reads and streams the 1s bars.
 */
export function resolutionToSeconds(resolution: string): number {
  if (resolution === CHART_TICK_RESOLUTION) return ChartTimeframe.CHART_TIMEFRAME_SEC_1
  return resolutionToChartTimeframe(resolution) ?? Number.NaN
}

/** Bar width in seconds → TV resolution; an off-grid width falls to the widest bar under it. */
export function secondsToResolution(seconds: number): ResolutionString {
  const exact = chartTimeframeToResolution(seconds)
  if (exact) return exact as ResolutionString
  const below = CHART_TIMEFRAMES.filter((t) => t.timeframe <= seconds)
  return (below[below.length - 1] ?? CHART_TIMEFRAMES[0]).resolution as ResolutionString
}

/** The chart's TV resolution: Tick's one-tick resolution, else the bar width's. */
export function chartResolution(timeframeSeconds: number, tick: boolean): ResolutionString {
  return tick ? (CHART_TICK_RESOLUTION as ResolutionString) : secondsToResolution(timeframeSeconds)
}

/** Collapse a bar time to whole seconds (TV bars carry ms; caches key by sec).
 *  Shared with own-fetch series descriptors for their ms→sec normalization. */
export function normalizeBarTimeSec(time: number): number {
  return time > 1000000000000 ? Math.floor(time / 1000) : time
}

// ---------------------------------------------------------------------------
// IChartDataProvider — exchange-specific implementation contract
// ---------------------------------------------------------------------------

export type AnalyticsBar = Bar & {
  analytics?: Partial<Record<ChartAnalyticsMetric, number>>
  rawAnalytics?: Partial<Record<string, number>>
  rawBar?: Bar
}

export type LiveQuoteUpdate = {
  lastPrice?: string | number
  bidPrice?: string | number
  askPrice?: string | number
}

function positiveFiniteNumber(value: string | number | undefined): number | undefined {
  if (value === undefined || value === '') return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

type SubscriberRecord = {
  routingSymbol: string
  comparisonSubscriptionKey?: string
  resolution: ResolutionString
  timeframeSeconds: number
  callback: SubscribeBarsCallback
}

type QuoteSubscriberRecord = {
  symbols: string[]
  fastSymbols: string[]
  callback: QuotesCallback
}

type AnalyticsCacheKeyContext = {
  symbol?: string
  // Optional — only meaningful for the quote-map write in storeAnalytics
  // below. The analytics cache group key (getCacheGroupKey) always keys off
  // `symbol` alone and never reads this.
  routingSymbol?: string
  timeframeSeconds?: number
}

export type CvdMeasure = 'base' | 'quote'

export type AnalyticsValueOptions = {
  /** Denomination selected by a study; defaults to 'base' (legacy behavior). */
  measure?: CvdMeasure
  /** Study input values, used by local calculations and own-fetch series cache keys. */
  inputs?: SeriesInputValues
}

// Bounds the history-wall probe below: wide enough to jump past a fluke-
// empty window, far cheaper than scanning to genesis (SAMPLE BY FILL(PREV)
// materializes one row per bucket across the whole probed span).
export const PROBE_WIDEN_FACTOR = 200
export const MAX_PROBE_LOOKBACK_SECONDS = 20 * 365 * 86400 // 20 years

// Caps distinct (symbol, timeframe) groups, not bars within a group — a
// group being actively panned must keep every bar it fetched (evicting by
// bar recency is what silently blanked out deep history before this fix).
// Evicting whole idle groups still bounds memory across a long session with
// many symbol/timeframe switches.
export const MAX_ANALYTICS_CACHE_GROUPS = 24

// Per-group safety valve, independent of the cap above: a single group is
// otherwise unbounded by design (see touchGroupOnWrite for why). 20,000
// bars covers ~54 years at 1D, ~2.3 years at 1H — far beyond any realistic
// pan — while still bounding a pathological long-lived session.
export const MAX_BARS_PER_GROUP = 20_000

// Amortizes trimGroupIfOversized's sort: without batching, a group parked at
// the cap by a long-running live-tick session re-sorts its whole member set
// on every single incoming bar just to evict one. See trimGroupIfOversized.
export const TRIM_BATCH = 1_000

// TradingView generally asks for roughly 200–300 bars on its first paint.
// Fetch a modest superset while the library initializes so getBars can reuse
// the in-flight response without making the cold path wait sequentially.
export const INITIAL_PRIME_BAR_COUNT = 500
const INITIAL_PRIME_TTL_MS = 15_000

type PrimedBarsRequest = {
  expiresAt: number
  from: number
  promise: Promise<AnalyticsBar[]>
  to: number
}

const toCandle = (bar: AnalyticsBar): CandleLike => ({
  timeSec: normalizeBarTimeSec(bar.time),
  open: bar.open,
  high: bar.high,
  low: bar.low,
  close: bar.close,
  volume: bar.volume ?? 0,
})

export interface IChartDataProvider {
  getSupportedResolutions(): ResolutionString[]
  resolveSymbol(identity: ChartSymbolIdentity, signal?: AbortSignal): Promise<LibrarySymbolInfo>
  fetchBars(
    params: {
      identity: ChartSymbolIdentity
      timeframe: number // seconds
      from: number // unix seconds
      to: number // unix seconds
      limit?: number
      families?: CandleFamily[]
    },
    signal?: AbortSignal
  ): Promise<AnalyticsBar[]>
  /** Drops a venue's carried-forward bars from hours it was closed; null keeps every bar. */
  getClosedMarketBarFilter?(
    identity: ChartSymbolIdentity,
    timeframe: number
  ): Promise<ClosedMarketBarFilter | null>
}

export interface ChartMarksRequest {
  identity: ChartSymbolIdentity
  from: number
  to: number
  resolution: ResolutionString
  timeframeSeconds: number
  barTimes: number[]
}

export type ChartMarksProvider = (request: ChartMarksRequest) => Mark[]

// ---------------------------------------------------------------------------
// ChartDatafeed — IExternalDatafeed implementation, exchange-agnostic
// ---------------------------------------------------------------------------

export class ChartDatafeed implements IExternalDatafeed {
  private readonly provider: IChartDataProvider
  private readonly subscribers = new Map<string, SubscriberRecord>()
  private readonly analyticsByKey = new Map<string, Partial<Record<ChartAnalyticsMetric, number>>>()
  private readonly rawAnalyticsByKey = new Map<string, Partial<Record<string, number>>>()
  private readonly rawBarsByKey = new Map<string, Bar>()
  private readonly latestQuoteBySymbol = new Map<string, QuoteData>()
  private latestCachedBarTime = 0
  private readonly primedBarsByKey = new Map<string, PrimedBarsRequest>()
  /** Keyed by routing symbol + timeframe; resolved in getBars, read by pushBar. */
  private readonly closedBarFilters = new Map<string, ClosedMarketBarFilter | null>()
  // Group key -> exact set of bar-times cached for that group. Also doubles
  // as LRU order: Map iterates in insertion order, and delete-then-set bumps
  // a group to the end (re-`.set()`ing an existing key does NOT move it).
  // Least-recently-touched is always `.keys().next().value`.
  private readonly groupMembers = new Map<string, Set<number>>()
  // Latest known bar-time per group, used only to tell a forward-moving
  // write (a new live tick) from a backward-pan write (older history just
  // fetched) — see touchGroupOnWrite.
  private readonly groupLatestTime = new Map<string, number>()
  // CVD is rebased to the first bar in the visible range. Keeping the raw
  // left-edge time (rather than a resolved cached bar) lets history that lands
  // after a left pan use the same anchor without another range event.
  private readonly cumulativeVolumeDeltaBaselineByGroup = new Map<string, number>()
  private readonly quoteSubscribers = new Map<string, QuoteSubscriberRecord>()
  readonly seriesManager: SeriesSubscriptionManager
  readonly comparisonSeriesManager = new ChartSeriesSubscriptionManager()
  private primaryIdentity: ChartSymbolIdentity = {
    exchange: Exchange.EXCHANGE_UNSPECIFIED,
    symbol: '',
  }
  private requestedFamilies: CandleFamily[] = []
  private marksProvider?: ChartMarksProvider

  constructor(provider: IChartDataProvider, seriesRegistry?: SeriesSourceRegistry) {
    this.provider = provider
    this.seriesManager = new SeriesSubscriptionManager(seriesRegistry ?? new SeriesSourceRegistry())
  }

  /**
   * The TV widget binds to a single datafeed/provider instance for its lifetime
   * (set once at init). When the active chart tab switches to a different exchange,
   * call this instead of recreating the datafeed so resolveSymbol/fetchBars pick up
   * the new exchange for the existing widget.
   */
  setPrimarySeries(identity: ChartSymbolIdentity): void {
    this.primaryIdentity = { ...identity }
  }

  /** @deprecated Use setPrimarySeries so symbol and exchange change atomically. */
  setExchange(exchange: Exchange): void {
    this.primaryIdentity = { ...this.primaryIdentity, exchange }
  }

  /** Forward the requested candle-family mask to the provider (empty = ALL). */
  setRequestedFamilies(families: CandleFamily[]): void {
    this.requestedFamilies = [...families]
  }

  /**
   * Start the primary series' first history request before TradingView calls
   * getBars. The exact family mask is part of the key, so a later study-family
   * widening can never consume an incomplete primed response.
   */
  primeInitialBars(identity: ChartSymbolIdentity, timeframe: number): Promise<AnalyticsBar[]> {
    const families = this.getFamiliesForIdentity(identity)
    const key = this.getPrimedBarsKey(identity, timeframe, families)
    const now = Date.now()
    for (const [cachedKey, request] of this.primedBarsByKey) {
      if (request.expiresAt <= now) this.primedBarsByKey.delete(cachedKey)
    }
    const existing = this.primedBarsByKey.get(key)
    if (existing) return existing.promise

    const to = Math.floor(Date.now() / 1000) + timeframe
    const marketClosurePadding = identity.exchange === Exchange.EXCHANGE_XKRX ? 7 * 86400 : 0
    const from = Math.max(
      0,
      to - Math.max(timeframe * INITIAL_PRIME_BAR_COUNT, marketClosurePadding)
    )
    const promise = this.provider
      .fetchBars({
        identity,
        timeframe,
        from,
        to,
        limit: INITIAL_PRIME_BAR_COUNT,
        families,
      })
      .catch((error) => {
        this.primedBarsByKey.delete(key)
        throw error
      })

    this.primedBarsByKey.set(key, {
      expiresAt: now + INITIAL_PRIME_TTL_MS,
      from,
      promise,
      to,
    })
    return promise
  }

  setMarksProvider(provider?: ChartMarksProvider): void {
    this.marksProvider = provider
  }

  onReady(callback: OnReadyCallback): void {
    setTimeout(
      () =>
        callback({
          supported_resolutions: this.provider.getSupportedResolutions(),
          exchanges: [],
          symbols_types: [],
          supports_marks: true,
          supports_time: false,
        }),
      0
    )
  }

  searchSymbols(
    _userInput: string,
    _exchange: string,
    _symbolType: string,
    onResult: SearchSymbolsCallback
  ): void {
    onResult([])
  }

  getMarks(
    symbolInfo: LibrarySymbolInfo,
    from: number,
    to: number,
    onDataCallback: GetMarksCallback<Mark>,
    resolution: ResolutionString
  ): void {
    try {
      const timeframeSeconds = resolutionToSeconds(resolution)
      onDataCallback(
        this.marksProvider?.({
          identity: this.resolveSymbolIdentity(symbolInfo),
          from,
          to,
          resolution,
          timeframeSeconds,
          barTimes: this.getCachedBarTimes(symbolInfo.name, timeframeSeconds, from, to),
        }) ?? []
      )
    } catch {
      onDataCallback([])
    }
  }

  async resolveSymbol(
    symbolName: string,
    onResolve: ResolveCallback,
    onError: DatafeedErrorCallback
  ): Promise<void> {
    const qualifiedIdentity = parseQualifiedChartSymbol(symbolName)
    const identity = qualifiedIdentity ?? { ...this.primaryIdentity, symbol: symbolName }
    try {
      const info = await this.provider.resolveSymbol(identity)
      const qualifiedSymbol = qualifyChartSymbol(identity)
      onResolve(
        qualifiedIdentity
          ? { ...info, name: qualifiedSymbol, ticker: qualifiedSymbol }
          : { ...info, name: symbolName, ticker: qualifiedSymbol }
      )
    } catch (e) {
      onError(String(e))
    }
  }

  async getBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    requestedPeriod: { from: number; to: number; firstDataRequest: boolean; countBack?: number },
    onResult: HistoryCallback,
    onError: DatafeedErrorCallback
  ): Promise<void> {
    // On the one-tick resolution (Tick) TV pages with fractional seconds (e.g. 1792042594.488);
    // the backend's from/to are int64 seconds. Widen to whole seconds so nothing is cut off.
    const periodParams = {
      ...requestedPeriod,
      from: Math.floor(requestedPeriod.from),
      to: Math.ceil(requestedPeriod.to),
    }
    const timeframe = resolutionToSeconds(resolution)
    const symbol = symbolInfo.name
    const identity = this.resolveSymbolIdentity(symbolInfo)

    const deliver = (bars: AnalyticsBar[], meta: { noData: boolean; nextTime?: number }): void => {
      const sorted = bars.slice().sort((a, b) => a.time - b.time)
      sorted.forEach((bar) => this.storeAnalytics(bar, { symbol, timeframeSeconds: timeframe }))
      onResult(
        sorted.map(({ analytics: _analytics, ...bar }) => bar),
        meta
      )
    }

    let bars: AnalyticsBar[]
    let usedPrimedRequest = false
    try {
      const families = this.getFamiliesForIdentity(identity)
      const primed = periodParams.firstDataRequest
        ? this.primedBarsByKey.get(this.getPrimedBarsKey(identity, timeframe, families))
        : undefined
      const overlapsPrimedWindow =
        primed && periodParams.to >= primed.from && periodParams.from <= primed.to
      if (primed && primed.expiresAt > Date.now() && overlapsPrimedWindow) {
        usedPrimedRequest = true
        const primedBars = await primed.promise
        bars = primedBars.filter((bar) => {
          const time = normalizeBarTimeSec(bar.time)
          return time >= periodParams.from && time <= periodParams.to
        })
      } else {
        bars = await this.provider.fetchBars({
          identity,
          timeframe,
          from: periodParams.from,
          to: periodParams.to,
          limit: periodParams.countBack,
          families,
        })
      }
    } catch (e) {
      // Priming is only an optimization. If it failed, preserve the normal
      // TradingView history path instead of surfacing a transient prefetch error.
      if (!usedPrimedRequest) {
        onError(String(e))
        return
      }
      try {
        bars = await this.provider.fetchBars({
          identity,
          timeframe,
          from: periodParams.from,
          to: periodParams.to,
          limit: periodParams.countBack,
          families: this.getFamiliesForIdentity(identity),
        })
      } catch (fallbackError) {
        onError(String(fallbackError))
        return
      }
    }

    const closedBarFilter = await this.resolveClosedBarFilter(identity, timeframe)
    if (closedBarFilter && bars.length > 0) {
      const kept = bars.filter((bar) => !closedBarFilter(toCandle(bar)))
      if (kept.length === 0) {
        // The whole window was the market being shut. Older history exists (these bars prove
        // it): page on from just before the earliest of them.
        const earliest = Math.min(...bars.map((bar) => bar.time))
        deliver([], { noData: true, nextTime: earliest - 1000 })
        return
      }
      bars = kept
    }

    if (bars.length > 0) {
      deliver(bars, { noData: false })
      return
    }

    // A single empty window isn't proof of "beginning of history" — TV
    // disables further backward requests for this resolution once noData:true
    // comes back with no nextTime.
    const requestedSpan = Math.max(periodParams.to - periodParams.from, timeframe)
    const widenSeconds = Math.min(requestedSpan * PROBE_WIDEN_FACTOR, MAX_PROBE_LOOKBACK_SECONDS)
    const probeFrom = Math.max(0, periodParams.from - widenSeconds)

    let probe: AnalyticsBar[]
    try {
      // Probe ends at `from`, not `to`: the backend's LIMIT -N returns the
      // newest row in range, so probing up to `to` would just re-find the
      // edge TV already has.
      probe = await this.provider.fetchBars({
        identity,
        timeframe,
        from: probeFrom,
        to: periodParams.from,
        limit: 1,
        families: this.getFamiliesForIdentity(identity),
      })
    } catch (e) {
      console.warn(`ChartDatafeed: getBars history probe failed for ${symbol}@${timeframe}s`, e)
      probe = []
    }

    if (probe.length > 0) {
      // nextTime is only honored alongside noData:true (TV's docs; this
      // repo's UDF reference impl, history-provider.ts) — nudged one bucket
      // so the next primary request's exclusive `timestamp < to` bound
      // doesn't re-exclude it.
      deliver([], { noData: true, nextTime: nextChartBarStartMs(probe[0].time, timeframe) })
    } else {
      deliver([], { noData: true })
    }
  }

  private async resolveClosedBarFilter(
    identity: ChartSymbolIdentity,
    timeframe: number
  ): Promise<ClosedMarketBarFilter | null> {
    const key = `${qualifyChartSymbol(identity)}|${timeframe}`
    if (this.closedBarFilters.has(key)) return this.closedBarFilters.get(key)!
    const filter =
      (await this.provider.getClosedMarketBarFilter?.(identity, timeframe).catch(() => null)) ??
      null
    this.closedBarFilters.set(key, filter)
    return filter
  }

  subscribeBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    onTick: SubscribeBarsCallback,
    subscriberUID: string
  ): void {
    const timeframeSeconds = resolutionToSeconds(resolution)
    const identity = this.resolveSymbolIdentity(symbolInfo)
    const routingSymbol = qualifyChartSymbol(identity)
    const isComparison = !areChartSymbolsEqual(identity, this.primaryIdentity)
    this.subscribers.set(subscriberUID, {
      routingSymbol,
      comparisonSubscriptionKey: isComparison
        ? this.comparisonSeriesManager.acquire({ identity, timeframeSeconds })
        : undefined,
      resolution,
      timeframeSeconds,
      callback: onTick,
    })
  }

  unsubscribeBars(subscriberUID: string): void {
    const subscriber = this.subscribers.get(subscriberUID)
    if (subscriber?.comparisonSubscriptionKey) {
      this.comparisonSeriesManager.release(subscriber.comparisonSubscriptionKey)
    }
    this.subscribers.delete(subscriberUID)
  }

  getQuotes(
    symbols: string[],
    onDataCallback: QuotesCallback,
    onErrorCallback: QuotesErrorCallback
  ): void {
    try {
      onDataCallback(symbols.map((symbol) => this.getQuote(symbol)))
    } catch (error) {
      onErrorCallback(String(error))
    }
  }

  subscribeQuotes(
    symbols: string[],
    fastSymbols: string[],
    onRealtimeCallback: QuotesCallback,
    listenerGUID: string
  ): void {
    this.quoteSubscribers.set(listenerGUID, {
      symbols,
      fastSymbols,
      callback: onRealtimeCallback,
    })

    onRealtimeCallback([...symbols, ...fastSymbols].map((symbol) => this.getQuote(symbol)))
  }

  unsubscribeQuotes(listenerGUID: string): void {
    this.quoteSubscribers.delete(listenerGUID)
  }

  /**
   * Publishes the actual top-of-book ticker for TradingView's native Buy/Sell
   * buttons. Primary symbols can be requested by TV as either the display name
   * (`BTC-PERPETUAL`) or the canonical ticker (`DERIBIT:BTC-PERPETUAL`), so both
   * aliases must receive a QuoteData object whose `n` exactly matches that alias.
   */
  pushQuote(identity: ChartSymbolIdentity, update: LiveQuoteUpdate): void {
    const lastPrice = positiveFiniteNumber(update.lastPrice)
    const bid = positiveFiniteNumber(update.bidPrice)
    const ask = positiveFiniteNumber(update.askPrice)
    const values: DatafeedQuoteValues = {}

    if (lastPrice !== undefined) values.lp = lastPrice
    if (bid !== undefined) values.bid = bid
    if (ask !== undefined) values.ask = ask
    if (bid !== undefined && ask !== undefined) values.spread = ask - bid
    if (Object.keys(values).length === 0) return

    this.publishQuote([qualifyChartSymbol(identity), identity.symbol], values)
  }

  fetchBars(
    params: {
      identity: ChartSymbolIdentity
      timeframe: number
      from: number
      to: number
      limit?: number
    },
    signal?: AbortSignal
  ): Promise<AnalyticsBar[]> {
    return this.provider.fetchBars(
      { ...params, families: this.getFamiliesForIdentity(params.identity) },
      signal
    )
  }

  /** Called by the component when a new bar arrives from WebSocket */
  pushBar(
    bar: AnalyticsBar,
    context?: { symbol?: string; routingSymbol?: string; timeframeSeconds?: number }
  ): void {
    // A live update while the market is shut is the same carried-forward filler as history's.
    const closedBarFilter =
      context?.routingSymbol !== undefined && context.timeframeSeconds !== undefined
        ? this.closedBarFilters.get(`${context.routingSymbol}|${context.timeframeSeconds}`)
        : undefined
    if (closedBarFilter?.(toCandle(bar))) return
    this.storeAnalytics(bar, context)
    const { analytics: _analytics, ...tvBar } = bar

    this.subscribers.forEach((subscriber) => {
      if (
        context?.routingSymbol !== undefined &&
        subscriber.routingSymbol !== context.routingSymbol
      ) {
        return
      }
      if (
        context?.timeframeSeconds !== undefined &&
        subscriber.timeframeSeconds !== context.timeframeSeconds
      ) {
        return
      }

      subscriber.callback(tvBar)
    })
  }

  getLatestCachedBarTime(): number {
    return this.latestCachedBarTime
  }

  setCumulativeVolumeDeltaBaseline(time: number, context?: AnalyticsCacheKeyContext): boolean {
    const normalizedTime = this.normalizeBarTime(time)
    if (!Number.isFinite(normalizedTime)) return false

    const groupKey = this.getCacheGroupKey(context)
    if (this.cumulativeVolumeDeltaBaselineByGroup.get(groupKey) === normalizedTime) return false

    this.cumulativeVolumeDeltaBaselineByGroup.set(groupKey, normalizedTime)
    return true
  }

  getAnalyticsValue(
    metric: ChartAnalyticsMetric,
    time: number,
    context?: AnalyticsCacheKeyContext,
    options?: AnalyticsValueOptions
  ): number | null {
    const normalizedTime = this.normalizeBarTime(time)
    if (this.seriesManager.hasMetric(metric)) {
      return this.seriesManager.getValue(metric, normalizedTime, {
        symbol: context?.symbol ?? '__unknown_symbol__',
        // main()'s context carries no exchange — use the datafeed's captured one
        // so the read key matches the key acquire used (which has ctx.exchange).
        exchange: this.primaryIdentity.exchange,
        timeframeSeconds: context?.timeframeSeconds ?? -1,
        inputs: options?.inputs,
      })
    }
    // A study reads this every redraw, not just when new bars arrive — a
    // group with no new bars for a while (e.g. a daily-timeframe study
    // between closes) must count as "in use" too, or it can get evicted by
    // unrelated symbol/timeframe activity while still on-screen.
    const groupKey = this.getCacheGroupKey(context)
    this.touchGroupOnRead(groupKey)
    const inputs = options?.inputs
    const measure = options?.measure ?? 'base'

    if (metric === 'cumulativeVolumeDelta') {
      return this.getCumulativeVolumeDelta(groupKey, normalizedTime, measure)
    }
    if (metric === 'vwap') {
      const lookbackWindow = this.getLookbackWindow(inputs, 1)
      return lookbackWindow === 1
        ? this.getCachedMetric(groupKey, normalizedTime, 'vwap')
        : this.getRollingVwap(groupKey, normalizedTime, lookbackWindow)
    }
    if (metric === 'parkinsonVolatility') {
      return this.getRollingRootMeanSquare(
        groupKey,
        normalizedTime,
        'parkinsonVolatility',
        this.getLookbackWindow(inputs, 1)
      )
    }
    if (metric === 'aggregatedFundingRateAvgClose') {
      return this.getRollingAverage(
        groupKey,
        normalizedTime,
        'fundingRate',
        this.getLookbackWindow(inputs, 10)
      )
    }
    if (metric === 'netBuyVolumeBase') {
      return this.getCachedMetric(
        groupKey,
        normalizedTime,
        `netBuyVolume${measure === 'quote' ? 'Quote' : 'Base'}` as ChartAnalyticsMetric
      )
    }
    if (metric === 'totalLiqBase') {
      return this.getCachedMetric(
        groupKey,
        normalizedTime,
        measure === 'quote' ? 'totalLiqQuote' : 'totalLiqBase'
      )
    }
    if (metric === 'netLiquidations' && measure === 'quote') {
      const baseValue = this.getCachedMetric(groupKey, normalizedTime, 'netLiquidations')
      return baseValue === null
        ? null
        : this.convertLiquidationBaseToQuote(groupKey, normalizedTime, baseValue)
    }
    if (metric === 'avgTradeSize' && measure === 'quote') {
      const quoteVolume = this.getCachedMetric(groupKey, normalizedTime, 'volumeQuote')
      const tradeCount = this.getCachedMetric(groupKey, normalizedTime, 'totalTradeCount')
      return quoteVolume !== null && tradeCount !== null && tradeCount > 0
        ? quoteVolume / tradeCount
        : null
    }
    if (
      metric === 'volumeBaseBlockIncl' ||
      metric === 'buyVolumeBaseBlockIncl' ||
      metric === 'sellVolumeBaseBlockIncl'
    ) {
      return this.getMergedVolumeValue(groupKey, normalizedTime, metric, measure, inputs)
    }

    return this.getCachedMetric(groupKey, normalizedTime, metric)
  }

  getRawAnalyticsValue(
    metric: string,
    time: number,
    context?: AnalyticsCacheKeyContext
  ): number | null {
    const normalizedTime = this.normalizeBarTime(time)
    const groupKey = this.getCacheGroupKey(context)
    this.touchGroupOnRead(groupKey)
    const analytics = this.rawAnalyticsByKey.get(`${groupKey}|${normalizedTime}`)
    const value = analytics?.[metric]
    return typeof value === 'number' && Number.isFinite(value) ? value : null
  }

  getLiquidationSideValue(
    side: 'buy' | 'sell',
    time: number,
    context?: AnalyticsCacheKeyContext,
    measure: CvdMeasure = 'base'
  ): number | null {
    const normalizedTime = this.normalizeBarTime(time)
    const groupKey = this.getCacheGroupKey(context)
    this.touchGroupOnRead(groupKey)
    const rawMetric = side === 'buy' ? 'buyLiqBase' : 'sellLiqBase'
    const baseValue = this.rawAnalyticsByKey.get(`${groupKey}|${normalizedTime}`)?.[rawMetric]
    if (typeof baseValue !== 'number' || !Number.isFinite(baseValue)) return null
    if (measure === 'base') return baseValue
    return this.convertLiquidationBaseToQuote(groupKey, normalizedTime, baseValue)
  }

  getPreviousAnalyticsValue(
    metric: ChartAnalyticsMetric,
    time: number,
    context?: AnalyticsCacheKeyContext,
    options?: AnalyticsValueOptions
  ): number | null {
    const normalizedTime = this.normalizeBarTime(time)
    const groupKey = this.getCacheGroupKey(context)
    const times = this.getWindowTimes(groupKey, normalizedTime, 2)
    const previousTime = times.length > 1 ? times[times.length - 2] : null
    return previousTime === null
      ? null
      : this.getAnalyticsValue(metric, previousTime, context, options)
  }

  getRawBarCloseValue(time: number, context?: AnalyticsCacheKeyContext): number | null {
    const normalizedTime = this.normalizeBarTime(time)
    const groupKey = this.getCacheGroupKey(context)
    this.touchGroupOnRead(groupKey)
    const rawBar = this.rawBarsByKey.get(`${groupKey}|${normalizedTime}`)
    const value = rawBar?.close
    return typeof value === 'number' && Number.isFinite(value) ? value : null
  }

  private storeAnalytics(bar: AnalyticsBar, context?: AnalyticsCacheKeyContext): void {
    const normalizedTime = this.normalizeBarTime(bar.time)
    const groupKey = this.getCacheGroupKey(context)
    const cacheKey = `${groupKey}|${normalizedTime}`
    this.latestCachedBarTime = Math.max(this.latestCachedBarTime, normalizedTime)

    // Candle data supplies last/OHLC values only. Bid/ask come from pushQuote's
    // actual ticker stream and are deliberately not replaced with a fake close
    // price whenever a candle update arrives.
    const quoteSymbols = [context?.routingSymbol, context?.symbol].filter(
      (value): value is string => Boolean(value)
    )
    if (quoteSymbols.length > 0) {
      this.publishQuote(quoteSymbols, {
        lp: bar.close,
        ch: bar.close - bar.open,
        chp: bar.open ? ((bar.close - bar.open) / bar.open) * 100 : 0,
        open_price: bar.open,
        high_price: bar.high,
        low_price: bar.low,
      })
    }

    if (bar.analytics) {
      const existing = this.analyticsByKey.get(cacheKey) ?? {}
      this.analyticsByKey.set(cacheKey, {
        ...existing,
        ...bar.analytics,
      })
    }

    if (bar.rawAnalytics) {
      const existingRaw = this.rawAnalyticsByKey.get(cacheKey) ?? {}
      this.rawAnalyticsByKey.set(cacheKey, {
        ...existingRaw,
        ...bar.rawAnalytics,
      })
    }

    if (bar.rawBar) {
      this.rawBarsByKey.set(cacheKey, bar.rawBar)
    } else {
      const { analytics: _analytics, rawAnalytics: _rawAnalytics, rawBar: _rawBar, ...tvBar } = bar
      this.rawBarsByKey.set(cacheKey, tvBar)
    }

    this.touchGroupOnWrite(groupKey, normalizedTime)
  }

  private getCacheGroupKey(context?: AnalyticsCacheKeyContext): string {
    const symbol = context?.symbol ?? '__unknown_symbol__'
    const timeframeSeconds = context?.timeframeSeconds ?? -1
    // `symbol` is the *unqualified* ticker for the primary series (no exchange
    // in it — see resolveSymbol/getBars), so the same ticker on two different
    // exchanges (e.g. BTC-PERP on Hyperliquid vs Lighter) would otherwise
    // collide on one cache group and keep serving the previous exchange's
    // analytics (CVD, VWAP, etc.) after a tab switch. Comparison-series symbols
    // are already exchange-qualified strings, so folding in the primary
    // exchange too is harmless there — worst case a benign cache miss.
    return `${this.primaryIdentity.exchange}|${symbol}|${timeframeSeconds}`
  }

  private getCumulativeVolumeDelta(
    groupKey: string,
    time: number,
    measure: CvdMeasure = 'base'
  ): number | null {
    const memberTimes = this.groupMembers.get(groupKey)
    if (!memberTimes) return null

    const values: Array<{ time: number; value: number }> = []
    for (const barTime of memberTimes) {
      if (barTime > time) continue

      const analytics = this.analyticsByKey.get(`${groupKey}|${barTime}`)
      const value = measure === 'quote' ? analytics?.netBuyVolumeQuote : analytics?.netBuyVolumeBase
      if (typeof value !== 'number' || !Number.isFinite(value)) continue

      values.push({ time: barTime, value })
    }

    if (values.length === 0) return null

    values.sort((a, b) => a.time - b.time)

    const requestedBaseline = this.cumulativeVolumeDeltaBaselineByGroup.get(groupKey)
    const anchorIndex =
      requestedBaseline === undefined
        ? 0
        : Math.max(
            0,
            values.findIndex(({ time: valueTime }) => valueTime >= requestedBaseline)
          )

    let cumulativeValue = 0
    let baseline = 0
    for (let index = 0; index < values.length; index += 1) {
      const { value } = values[index]
      cumulativeValue += value
      if (index === anchorIndex) baseline = cumulativeValue
    }

    return cumulativeValue - baseline
  }

  private getCachedMetric(
    groupKey: string,
    time: number,
    metric: ChartAnalyticsMetric
  ): number | null {
    const value = this.analyticsByKey.get(`${groupKey}|${time}`)?.[metric]
    return typeof value === 'number' && Number.isFinite(value) ? value : null
  }

  private convertLiquidationBaseToQuote(
    groupKey: string,
    time: number,
    baseValue: number
  ): number | null {
    const totalBase = this.getCachedMetric(groupKey, time, 'totalLiqBase')
    const totalQuote = this.getCachedMetric(groupKey, time, 'totalLiqQuote')
    if (totalBase === null || totalQuote === null || totalBase <= 0) return null
    return baseValue * (totalQuote / totalBase)
  }

  private getLookbackWindow(inputs: SeriesInputValues | undefined, fallback: number): number {
    const value = Number(inputs?.lookbackWindow ?? fallback)
    return Number.isFinite(value) ? Math.max(1, Math.min(10_000, Math.floor(value))) : fallback
  }

  private getWindowTimes(groupKey: string, time: number, length: number): number[] {
    const memberTimes = this.groupMembers.get(groupKey)
    if (!memberTimes) return []
    return [...memberTimes]
      .filter((barTime) => barTime <= time)
      .sort((a, b) => a - b)
      .slice(-length)
  }

  private getRollingVwap(groupKey: string, time: number, length: number): number | null {
    let baseVolume = 0
    let quoteVolume = 0
    for (const barTime of this.getWindowTimes(groupKey, time, length)) {
      const base = this.getCachedMetric(groupKey, barTime, 'volumeBase')
      const quote = this.getCachedMetric(groupKey, barTime, 'volumeQuote')
      if (base === null || quote === null) continue
      baseVolume += base
      quoteVolume += quote
    }
    return baseVolume > 0 ? quoteVolume / baseVolume : null
  }

  private getRollingAverage(
    groupKey: string,
    time: number,
    metric: ChartAnalyticsMetric,
    length: number
  ): number | null {
    let sum = 0
    let count = 0
    for (const barTime of this.getWindowTimes(groupKey, time, length)) {
      const value = this.getCachedMetric(groupKey, barTime, metric)
      if (value === null) continue
      sum += value
      count += 1
    }
    return count > 0 ? sum / count : null
  }

  private getRollingRootMeanSquare(
    groupKey: string,
    time: number,
    metric: ChartAnalyticsMetric,
    length: number
  ): number | null {
    let squaredSum = 0
    let count = 0
    for (const barTime of this.getWindowTimes(groupKey, time, length)) {
      const value = this.getCachedMetric(groupKey, barTime, metric)
      if (value === null) continue
      squaredSum += value ** 2
      count += 1
    }
    return count > 0 ? Math.sqrt(squaredSum / count) : null
  }

  private getMergedVolumeValue(
    groupKey: string,
    time: number,
    metric: 'volumeBaseBlockIncl' | 'buyVolumeBaseBlockIncl' | 'sellVolumeBaseBlockIncl',
    measure: CvdMeasure,
    inputs: SeriesInputValues | undefined
  ): number | null {
    const includeBlockTrades =
      inputs?.includeBlockTrades === true || inputs?.includeBlockTrades === 'true'
    const suffix = includeBlockTrades ? 'BlockIncl' : ''
    const unit = measure === 'quote' ? 'Quote' : 'Base'

    if (metric === 'volumeBaseBlockIncl') {
      return this.getCachedMetric(groupKey, time, `volume${unit}${suffix}` as ChartAnalyticsMetric)
    }

    if (measure === 'base') {
      const side = metric === 'buyVolumeBaseBlockIncl' ? 'buy' : 'sell'
      return this.getCachedMetric(
        groupKey,
        time,
        `${side}VolumeBase${suffix}` as ChartAnalyticsMetric
      )
    }

    const total = this.getCachedMetric(
      groupKey,
      time,
      `volumeQuote${suffix}` as ChartAnalyticsMetric
    )
    const net = this.getCachedMetric(
      groupKey,
      time,
      `netBuyVolumeQuote${suffix}` as ChartAnalyticsMetric
    )
    if (total === null || net === null) return null
    return metric === 'buyVolumeBaseBlockIncl' ? (total + net) / 2 : (total - net) / 2
  }

  /** Write path: records that `time` belongs to `groupKey`, bumps the group
   *  to most-recently-used, trims the group if it's grown past the
   *  per-group safety valve, then evicts the least-recently-touched group
   *  if the group itself is new and pushed the group count over the cap. */
  private touchGroupOnWrite(groupKey: string, time: number): void {
    const times = this.groupMembers.get(groupKey) ?? new Set<number>()
    const isNewGroup = !this.groupMembers.has(groupKey)
    const priorLatest = this.groupLatestTime.get(groupKey)
    // Only a write that extends the group's known range FORWARD (a new live
    // tick) is a candidate for trimming. A backward-pan write (getBars
    // fetching older history) must never trim — the group can already be at
    // the cap from a prior deep pan, and trimming here would delete the very
    // bars this write just fetched, reproducing the original bug at a much
    // higher threshold instead of fixing it.
    const isForwardMove = priorLatest === undefined || time > priorLatest
    if (isForwardMove) this.groupLatestTime.set(groupKey, time)

    times.add(time)
    this.bumpGroupOrder(groupKey, times)
    if (isForwardMove) this.trimGroupIfOversized(groupKey, times)

    if (isNewGroup && this.groupMembers.size > MAX_ANALYTICS_CACHE_GROUPS) {
      const staleGroupKey = this.groupMembers.keys().next().value
      if (staleGroupKey !== undefined) this.clearGroup(staleGroupKey)
    }
  }

  /** Read path: a group that's still being read (even with no new bars, e.g.
   *  a daily study between closes) is still in use. Bumping an already-
   *  resident group can never grow the map, so no eviction check is needed
   *  here — and a group with nothing cached yet has nothing to protect. */
  private touchGroupOnRead(groupKey: string): void {
    const times = this.groupMembers.get(groupKey)
    if (times === undefined) return
    this.bumpGroupOrder(groupKey, times)
  }

  /** Moves `groupKey` to the most-recently-used end of `groupMembers`'
   *  insertion order. Map preserves a key's original position across a
   *  same-key `.set()` — only delete-then-set actually moves it. */
  private bumpGroupOrder(groupKey: string, times: Set<number>): void {
    this.groupMembers.delete(groupKey)
    this.groupMembers.set(groupKey, times)
  }

  /** Only trims once the group is `TRIM_BATCH` past the cap, then trims back
   *  down to the cap in one pass — a group parked exactly at the cap by a
   *  long live-tick session would otherwise re-sort its entire (up to
   *  `MAX_BARS_PER_GROUP`-sized) member set on every single incoming bar
   *  just to evict one. Batching amortizes that sort over `TRIM_BATCH`
   *  writes; the group's real ceiling becomes `MAX_BARS_PER_GROUP +
   *  TRIM_BATCH`, negligibly different from the stated cap. */
  private trimGroupIfOversized(groupKey: string, times: Set<number>): void {
    if (times.size <= MAX_BARS_PER_GROUP + TRIM_BATCH) return
    const oldestFirst = Array.from(times).sort((a, b) => a - b)
    const toTrim = oldestFirst.slice(0, times.size - MAX_BARS_PER_GROUP)
    for (const time of toTrim) {
      times.delete(time)
      const cacheKey = `${groupKey}|${time}`
      this.analyticsByKey.delete(cacheKey)
      this.rawAnalyticsByKey.delete(cacheKey)
      this.rawBarsByKey.delete(cacheKey)
    }
  }

  private clearGroup(groupKey: string): void {
    const times = this.groupMembers.get(groupKey)
    if (times) {
      for (const time of times) {
        const cacheKey = `${groupKey}|${time}`
        this.analyticsByKey.delete(cacheKey)
        this.rawAnalyticsByKey.delete(cacheKey)
        this.rawBarsByKey.delete(cacheKey)
      }
    }
    this.groupMembers.delete(groupKey)
    this.groupLatestTime.delete(groupKey)
    this.cumulativeVolumeDeltaBaselineByGroup.delete(groupKey)
  }

  private normalizeBarTime(time: number): number {
    return normalizeBarTimeSec(time)
  }

  private resolveSymbolIdentity(symbolInfo: LibrarySymbolInfo): ChartSymbolIdentity {
    const qualified =
      parseQualifiedChartSymbol(symbolInfo.ticker ?? '') ??
      parseQualifiedChartSymbol(symbolInfo.name)
    return qualified ?? { ...this.primaryIdentity, symbol: symbolInfo.name }
  }

  private getCachedBarTimes(
    symbol: string,
    timeframeSeconds: number,
    from: number,
    to: number
  ): number[] {
    const times = this.groupMembers.get(this.getCacheGroupKey({ symbol, timeframeSeconds }))
    if (!times) return []
    return [...times].filter((time) => time >= from && time <= to).sort((a, b) => a - b)
  }

  private getFamiliesForIdentity(identity: ChartSymbolIdentity): CandleFamily[] {
    return areChartSymbolsEqual(identity, this.primaryIdentity)
      ? [...this.requestedFamilies]
      : [CandleFamily.CANDLE_FAMILY_CORE]
  }

  private getPrimedBarsKey(
    identity: ChartSymbolIdentity,
    timeframe: number,
    families: readonly CandleFamily[]
  ): string {
    return `${identity.exchange}|${identity.symbol}|${timeframe}|${families.join(',')}`
  }

  private getQuote(symbol: string): QuoteData {
    const direct = this.latestQuoteBySymbol.get(symbol)
    if (direct) return direct

    // Saved layouts and the primary series may ask for the plain display name
    // even though resolveSymbol.ticker is exchange-qualified (or vice versa).
    const qualified = parseQualifiedChartSymbol(symbol)
    const alias = qualified
      ? qualified.symbol
      : symbol === this.primaryIdentity.symbol
        ? qualifyChartSymbol(this.primaryIdentity)
        : undefined
    const aliased = alias ? this.latestQuoteBySymbol.get(alias) : undefined
    if (aliased?.s === 'ok') {
      return { ...aliased, n: symbol, v: { ...aliased.v, original_name: symbol } }
    }

    return { n: symbol, s: 'ok', v: {} }
  }

  private publishQuote(symbols: string[], values: DatafeedQuoteValues): void {
    for (const symbol of new Set(symbols)) {
      const existing = this.latestQuoteBySymbol.get(symbol)
      const previousValues = existing?.s === 'ok' ? existing.v : {}
      const quote: QuoteData = {
        n: symbol,
        s: 'ok',
        v: {
          ...previousValues,
          ...values,
          original_name: symbol,
        },
      }
      this.latestQuoteBySymbol.set(symbol, quote)
      this.notifyQuoteSubscribers(symbol, quote)
    }
  }

  private notifyQuoteSubscribers(symbol: string, quote: QuoteData): void {
    this.quoteSubscribers.forEach(({ symbols, fastSymbols, callback }) => {
      if (!symbols.includes(symbol) && !fastSymbols.includes(symbol)) return
      callback([quote])
    })
  }
}
